import React, { useCallback } from "react";
import { Card, Icon, Badge, Tooltip, message } from "antd";
import { Link } from "react-router-dom";
import copy from "copy-to-clipboard";
import * as R from "ramda";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import * as gtag from "../lib/gtag";
import { getOptimizedOpportunityImage, httpToHttps } from "../utils/images";
import { OPPORTUNITY_STATUSES } from "../constants/statuses";

import Button from "./Button";
import StatusIcon from "./StatusIcon";

import styled from "styled-components/macro";

const { Meta } = Card;

const getThumbnail = (thumbnail, text) => {
  const extension = thumbnail && thumbnail.split(".").pop();
  return httpToHttps(
    thumbnail
      ? getOptimizedOpportunityImage(thumbnail.replace(extension, "jpg"))
      : `https://fakeimg.pl/350x200/eeeeee/000/?text=${text}&font=museo&font_size=32`
  );
};

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  filter: grayscale(
    ${props =>
      props.status === "finished" || props.status === "closed" ? 1 : 0}
  );
  transition: 0.3s ease;
  &:hover {
    filter: grayscale(0);
    .ant-card-bordered {
      box-shadow: 0px 8px 15px -3px gainsboro;
    }
  }

  .ant-card-head {
    min-height: 75px;
    padding: 0 16px;
  }

  .ant-card-cover {
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-end;
    height: 177px;
    font-size: 1rem;
    width: 100%;
    .cover {
      position: fixed;
      color: ${({ theme }) => theme["@light"]};
    }
  }
  .ant-card-bordered {
    border: none;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
    transition: 0.3s ease;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .ant-card-body {
    padding: 10px;
    width: 100%;
    flex: 1;
  }
  .cover {
    .status {
      position: absolute;
      transform: rotate(-45deg);
      width: 150px;
      text-align: center;
      left: -47px;
      top: 18px;
      color: #fff;
      font-weight: bold;
      font-size: 0.6em;
      padding: 2px 4px;
      background-color: ${({ theme, status }) =>
        theme[`@${(OPPORTUNITY_STATUSES[status] || {}).color}`]};
      color: ${({ theme }) => theme["@light"]};
      box-shadow: -1px 1px 9px 0px #00000091;
    }
  }

  .meta {
    .description {
      display: block;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      font-size: 0.75em;
    }

    .icons {
      display: flex;
      justify-content: space-around;
      margin-top: 8px;

      .container {
        display: flex;
        flex-direction: column;
        align-items: center;

        .icon {
          font-size: 1.5em;
          color: #747d84;
        }

        .label {
          margin-top: 4px;
          font-size: 10px;
          color: ${({ theme }) => theme["@gray-80"]};
        }
      }
    }
  }
`;

const CountMetadata = styled.h5`
  margin-top: 4px;
  font-size: 12px;
  font-weight: ${({ count }) => (count > 0 ? "bold" : "normal")};
  color: #747d84;
`;

const MainImage = styled.figure`
  background: url(${({ thumbnail, text }) => getThumbnail(thumbnail, text)});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  height: 177px;
  margin: 0;
  transition: 0.3s ease;
  cursor: pointer;
  &:hover {
    filter: brightness(105%) saturate(105%) contrast(105%);
  }
`;

const WrapperStatus = styled.div`
  position: absolute;
  top: -15px;
  right: 15px;
  z-index: 10;
`;

const Title = styled.p`
  font-size: 14px;
  font-weight: normal;
  text-align: left;
  color: ${({ theme }) => theme["@gray-80"]} !important;
  margin-bottom: 0;
  transition: 0.3s ease;
  white-space: break-spaces;
  line-height: 21px;

  /* Limit to 2 lines */
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  display: -webkit-box;
  height: 3em;

  &:hover {
    opacity: 0.7;
  }
`;

const CopyLinkButton = styled(Button)`
  &,
  &.ant-btn.ant-btn-sm {
    display: flex;
    align-items: center;
    font-size: 9px;
    height: 20px;
    max-width: 80px;
    padding-left: 6.6px;
    padding-right: 4px;
    pointer-events: auto;
    border-color: #d0d9ff !important;

    /* Icon styles */
    .anticon.anticonlink svg {
      fill: ${({ theme }) => theme["@primary"]} !important;
    }
    & > .anticon + span,
    & > span + .anticon {
      margin-left: 4px;
    }
  }
`;

const TeamName = styled.h5`
  font-size: 9px;
  font-style: ${({ team }) => (!team ? "italic" : "normal")};
  color: ${({ theme, team }) =>
    team ? theme["@gray-80"] : theme["@gray-50"]} !important;
  font-weight: normal;
`;

const OpportunityCard = ({
  id,
  title,
  video,
  image,
  status,
  matchCount = 0,
  team,
  notifications = []
}) => {
  const { t } = useTranslation();
  const teams = useSelector(({ teams }) => teams || []);
  const handleCopyOpportunityLink = useCallback(() => {
    copy(`${window.location.href}/${id}`);
    message.success(t("components:OpportunityCard.copiedOpportunityId"));
  }, [id, t]);

  const teamName = team
    ? R.find(R.propEq("id", team), teams).name
    : t("global:noTeam");

  return (
    <Container
      status={status}
      thumbnail={video || image}
      data-test={`opportunity-card-${status}`}
      className={status}
    >
      <Card
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 4
              }}
            >
              <div>
                <TeamName team={team}>{teamName}</TeamName>
              </div>
              <div>
                <Tooltip
                  title={t("components:OpportunityCard.copyOpportunityId", {
                    id
                  })}
                  overlayStyle={{
                    maxWidth: "none"
                  }}
                >
                  <CopyLinkButton
                    data-test={`copy-opportunity-link-${id}`}
                    icon="link"
                    onClick={() => {
                      handleCopyOpportunityLink();
                      gtag.event({
                        category: "Vacancies",
                        action: "Vacancies.VacancyCard.CopyLink",
                        label: id
                      });
                    }}
                    size="small"
                    shape="round"
                  >
                    <span style={{ color: "#343A40" }}>
                      ...{R.takeLast(8, id)}
                    </span>
                  </CopyLinkButton>
                </Tooltip>
              </div>
            </div>
            <div style={{ width: "100%" }}>
              <Link to={`/oportunidades/${id}`}>
                <Title title={title}>{title}</Title>
              </Link>
            </div>
          </div>
        }
        cover={
          <div
            className="cover"
            onClick={() =>
              gtag.event({
                category: "Vacancies",
                action: "Vacancies.VacancyCard.Click",
                label: id
              })
            }
          >
            <WrapperStatus>
              <StatusIcon status={status} />
            </WrapperStatus>
            <Link to={`/oportunidades/${id}`}>
              <MainImage
                text={t("global:opportunity")}
                thumbnail={video || image}
              />
            </Link>
          </div>
        }
      >
        <Meta
          description={
            <div className="meta">
              <div className="icons">
                <Link
                  to={`/oportunidades/${id}/candidatos`}
                  className="container"
                >
                  <Badge
                    data-test="new-likes"
                    key="user-add"
                    count={notifications.candidates}
                    dot
                  >
                    <Icon
                      className="icon"
                      type="solution"
                      onClick={() =>
                        gtag.event({
                          category: "Vacancies",
                          action: "Vacancies.VacancyCard.Pending",
                          label: id
                        })
                      }
                    />
                  </Badge>
                  <span className="label">
                    {t("components:OpportunityCard.new")}
                  </span>
                  <CountMetadata count={notifications.candidates}>
                    {notifications.candidates}
                  </CountMetadata>
                </Link>
                <Link to={`/oportunidades/${id}/proceso`} className="container">
                  <Badge
                    data-test="new-matches"
                    key="usergroup-add"
                    count={notifications.matches}
                    dot
                  >
                    <Icon
                      className="icon"
                      type="team"
                      onClick={() =>
                        gtag.event({
                          category: "Vacancies",
                          action: "Vacancies.VacancyCard.Matches",
                          label: id
                        })
                      }
                    />
                  </Badge>
                  <span className="label">
                    {t("components:OpportunityCard.matches")}
                  </span>
                  <CountMetadata count={matchCount}>{matchCount}</CountMetadata>
                </Link>
                <Link to={`/oportunidades/${id}/proceso`} className="container">
                  <Badge
                    data-test="new-messages"
                    key="message"
                    count={notifications.messages}
                    dot
                  >
                    <Icon
                      className="icon"
                      type="message"
                      onClick={() =>
                        gtag.event({
                          category: "Vacancies",
                          action: "Vacancies.VacancyCard.Messages",
                          label: id
                        })
                      }
                    />
                  </Badge>
                  <span className="label">{t("global:messages")}</span>
                  <CountMetadata count={notifications.messages}>
                    {notifications.messages}
                  </CountMetadata>
                </Link>
              </div>
            </div>
          }
        />
      </Card>
    </Container>
  );
};

export default OpportunityCard;
